/**
 * TabLink Component
 *
 * A drop-in replacement for react-router-dom Link that integrates with the tab navigation system.
 *
 * Usage:
 * ```tsx
 * // Opens in new tab and focuses it (default)
 * <TabLink to={`/users/${id}`}>View User</TabLink>
 *
 * // Navigates within current tab (like regular Link)
 * <TabLink to="/dashboard" newTab={false}>Dashboard</TabLink>
 *
 * // Modifier keys (Ctrl/Cmd/Shift) open in background tab (no focus)
 * // Middle-click also opens in background tab
 * ```
 */

import { useTabsContextSafe } from '@/context/TabsContext';
import { useTabLocation } from '@/context/TabLocationContext';
import { getRouteTitle } from '@/utils/routeUtils';
import React, { useCallback } from 'react';
import { history } from '@/utils/navigation';

/** Route destination - can be a string path or an object with pathname/search/hash */
type ToValue = string | { pathname?: string; search?: string; hash?: string };

type TabLinkProps = {
  /** Route path to navigate to (string or object like react-router-dom's Link) */
  to: ToValue;
  /** Children to render */
  children: React.ReactNode;
  /** Custom title for the tab */
  title?: string;
  /** Open in new tab (default: true) */
  newTab?: boolean;
  /** Replace current tab's history entry */
  replace?: boolean;
  /** Config-driven navigation behavior (overrides newTab/replace when set) */
  behavior?: 'new-tab' | 'replace' | 'reuse';
  /** Additional className */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
  /** onClick handler (called after navigation) */
  onClick?: (e: React.MouseEvent) => void;
  /** Prevent default behavior */
  disabled?: boolean;
  /** Accessible label for icon/action-style links */
  'aria-label'?: string;
  /** Stable selector for tests */
  'data-testid'?: string;
};

/**
 * Extracts the path and search string from a ToValue
 */
function extractPath(to: ToValue): { path: string; search: string } {
  if (typeof to === 'string') {
    // Handle string with potential query string
    const queryIndex = to.indexOf('?');
    if (queryIndex >= 0) {
      return { path: to.substring(0, queryIndex), search: to.substring(queryIndex) };
    }
    return { path: to, search: '' };
  }
  // Handle object syntax
  const path = to.pathname || '/';
  const search = to.search || '';
  return { path, search };
}

export const TabLink: React.FC<TabLinkProps> = ({
  to,
  children,
  title: customTitle,
  newTab = true,
  replace = false,
  behavior,
  className,
  style,
  onClick,
  disabled = false,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) => {
  const context = useTabsContextSafe();
  const tabLocation = useTabLocation();
  const paneId = tabLocation?.paneId;
  
  // Extract path and search from the 'to' prop (handles both string and object syntax)
  const { path: safePath, search: extractedSearch } = extractPath(to);
  const title = customTitle || getRouteTitle(safePath);
  const fullPath = extractedSearch ? `${safePath}${extractedSearch}` : safePath;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // If tabs not enabled, fall back to regular navigation
      if (!context) {
        if (replace) {
          history.replace(fullPath);
        } else {
          history.push(fullPath);
        }
        onClick?.(e);
        return;
      }

      // Any modifier key = open in background (no focus) AND force new tab
      const openInBackground = e.ctrlKey || e.metaKey || e.shiftKey;

      // Resolve effective behavior: explicit behavior prop > newTab/replace props
      const effectiveBehavior = behavior ?? (newTab ? 'reuse' : 'replace');

      if (openInBackground) {
        // Modifier key held: always create a new tab (bypass dedup), open in background
        context.openTab(safePath, title, { focus: false, forceNew: true, search: extractedSearch || undefined, paneId });
      } else if (effectiveBehavior === 'new-tab') {
        context.openTab(safePath, title, { focus: true, forceNew: true, search: extractedSearch || undefined, paneId });
      } else if (effectiveBehavior === 'replace') {
        context.navigateInTab(safePath, { title, replace, search: extractedSearch || undefined, paneId });
      } else {
        context.openTab(safePath, title, { focus: true, search: extractedSearch || undefined, paneId });
      }

      onClick?.(e);
    },
    [safePath, fullPath, extractedSearch, title, newTab, replace, behavior, disabled, context, onClick, paneId]
  );

  const handleMiddleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) return;

      // Middle click always opens in background tab (no focus) and forces new tab
      if (e.button === 1) {
        e.preventDefault();
        e.stopPropagation();
        if (context) {
          context.openTab(safePath, title, { focus: false, forceNew: true, search: extractedSearch || undefined, paneId });
        } else {
          // Fallback: open in new browser tab
          window.open(fullPath, '_blank');
        }
      }
    },
    [safePath, fullPath, extractedSearch, title, disabled, context, paneId]
  );

  return (
    <a
      href={fullPath}
      className={className}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      onClick={handleClick}
      onMouseDown={handleMiddleClick}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      role="link"
      tabIndex={disabled ? -1 : 0}
    >
      {children}
    </a>
  );
};

/**
 * Higher-order component to make any component open in a new tab when clicked
 */
export function withTabNavigation<P extends object>(
  Component: React.ComponentType<P>,
  getPath: (props: P) => string,
  getTitle?: (props: P) => string
) {
  return function TabNavigationWrapper(props: P) {
    const context = useTabsContextSafe();
    const tabLocation = useTabLocation();
    const paneId = tabLocation?.paneId;

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        const path = getPath(props);
        const title = getTitle?.(props) || getRouteTitle(path);

        // Any modifier key = open in background (no focus) AND force new tab
        const openInBackground = e.ctrlKey || e.metaKey || e.shiftKey;

        e.preventDefault();
        if (context) {
          if (openInBackground) {
            // Modifier key held: always create a new tab (bypass dedup), open in background
            context.openTab(path, title, { focus: false, forceNew: true, paneId });
          } else {
            context.openTab(path, title, { focus: true, paneId });
          }
        } else {
          history.push(path);
        }
      },
      [props, context, paneId]
    );

    return <Component {...props} onClick={handleClick} />;
  };
}

// Export as Link for drop-in replacement of react-router-dom Link
export { TabLink as Link };

export default TabLink;
